import React, { useMemo, useState } from 'react';
import MobileSearchBar from './MobileSearchBar';
import MobileEventCard from './MobileEventCard';
import MobileEmptyState from './MobileEmptyState';
import MobileLoadingSkeleton from './MobileLoadingSkeleton';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
};

const ORG_FILTERS = ['All', 'UPCI', 'WPF', 'ALJC', 'AFF', 'AIM', 'PAW'];
const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' },
];

const MobileDiscover = ({ events = [], savedEvents = [], onSaveToggle }) => {
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...events]
      .filter((e) => {
        const d = parseLocalDate(e.end || e.start || e.date);
        if (!d) return false;
        if (showPast ? d >= today : d < today) return false;
        if (e.status === 'cancelled') return showPast;
        return true;
      })
      .filter((e) => {
        if (orgFilter !== 'All' && (e.org || '').toUpperCase() !== orgFilter) return false;
        if (typeFilter !== 'all' && e.event_type !== typeFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          const haystack = [e.event, e.title, e.city, e.venue, e.org, e.description]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const da = parseLocalDate(a.start || a.date);
        const db = parseLocalDate(b.start || b.date);
        return showPast ? db - da : da - db;
      });
  }, [events, showPast, orgFilter, typeFilter, search]);

  const loading = events.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-4">
      {/* Header */}
      <header
        className="px-4 pb-4 bg-gradient-to-br from-primary-dark to-primary text-white rounded-b-3xl shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' }}
      >
        <h1 className="font-display text-2xl font-bold mb-1">Discover Events</h1>
        <p className="text-white/70 text-sm mb-4">Find your next conference experience</p>
        <MobileSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search events, cities, organizations..."
        />
      </header>

      {/* Filter chips */}
      <div className="px-4 mt-4">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTypeFilter(t.value)}
              className={`chip px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                typeFilter === t.value
                  ? 'bg-primary text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm'
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 shrink-0" />
          {ORG_FILTERS.map((org) => (
            <button
              key={org}
              onClick={() => setOrgFilter(org)}
              className={`chip px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                orgFilter === org
                  ? 'bg-primary text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm'
              }`}
            >
              {org}
            </button>
          ))}
        </div>
        {/* Past / Upcoming toggle */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowPast(false)}
            className={`chip flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${
              !showPast ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 shadow-sm'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setShowPast(true)}
            className={`chip flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${
              showPast ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 shadow-sm'
            }`}
          >
            Past
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="px-4 mt-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {loading ? 'Loading...' : `${filtered.length} event${filtered.length !== 1 ? 's' : ''} found`}
        </p>
      </div>

      {/* Event list */}
      <div className="mt-3 px-4 space-y-3">
        {loading ? (
          <MobileLoadingSkeleton variant="card" count={4} />
        ) : filtered.length === 0 ? (
          <MobileEmptyState
            icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
            title="No events found"
            message="Try adjusting your search or filters to find events."
          />
        ) : (
          filtered.map((e) => (
            <MobileEventCard
              key={e.id}
              event={e}
              onSaveToggle={onSaveToggle}
              isSaved={savedEvents.includes(e.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MobileDiscover;
